import { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { HelpIcon } from "@/components/shared/HelpIcon";
import { ErrorTextComponent } from "../error-text-component";
import { HelperTextComponent } from "../helper-text-component";

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  containerClassName?: string;
  tooltip?: boolean;
  tooltipMessage?: React.ReactNode;
  defaultOpen?: boolean;
  ghostText?: string;
  ghostBadge?: string;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      required,
      helperText,
      containerClassName,
      className,
      tooltip,
      tooltipMessage,
      defaultOpen = false,
      id,
      ghostText,
      ghostBadge,
      value,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const inputId = id || props.name || Math.random().toString(36).substr(2, 9);

    return (
      <div className={cn("space-y-2", containerClassName)}>
        <div className="flex h-[20px] items-center gap-2">
          {label && (
            <Label
              htmlFor={inputId}
              className={cn(
                "text-sm font-medium text-foreground transition-colors",
                error && "text-destructive",
              )}
            >
              {label}
              {required && <span className="mr-1 text-destructive">*</span>}
            </Label>
          )}

          {tooltip && (
            <div className="mt-0.5">
              <HelpIcon tooltipMessage={tooltipMessage} defaultOpen={defaultOpen} />
            </div>
          )}
        </div>

        {/* Container relativo para posicionar o Ghost Text atrás do Input */}
        <div className="relative flex items-center">
          <Input
            id={inputId}
            ref={ref}
            value={value}
            // Suprime o placeholder nativo quando o ghost text já ocupa esse espaço
            placeholder={ghostText && !value ? "" : placeholder}
            className={cn(
              "z-10 min-h-10 bg-transparent transition-all",
              error
                ? "border-destructive focus-visible:ring-destructive"
                : "border-input hover:border-accent-foreground/30",
              ghostText && "pr-16",
              className,
            )}
            {...props}
          />

          {/* Ghost text + badge do motivo */}
          {ghostText && (
            <div className="pointer-events-none absolute inset-0 z-0 flex items-center overflow-hidden px-3 text-sm">
              {/* Espaçador invisível com a largura exata do texto digitado */}
              <span className="whitespace-pre opacity-0">{value}</span>
              {/* Parte restante da sugestão */}
              <span className="whitespace-pre text-muted-foreground/60">{ghostText}</span>
              {/* Motivo/badge da sugestão */}
              {ghostBadge && (
                <>
                  <span className="mx-2 select-none text-xs text-muted-foreground/40">·</span>
                  <span className="truncate text-xs text-muted-foreground/50">{ghostBadge}</span>
                </>
              )}
            </div>
          )}

          {/* Badge "Tab ⇥" fixo no lado direito */}
          {ghostText && (
            <div className="pointer-events-none absolute right-3 top-1/2 z-20 -translate-y-1/2">
              <kbd className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground shadow-sm">
                Tab ⇥
              </kbd>
            </div>
          )}
        </div>

        <ErrorTextComponent error={error} />
        <HelperTextComponent text={helperText} error={error} />
      </div>
    );
  },
);

FormInput.displayName = "FormInput";
